/**
 * Admin → Spend Guard. Shows every paid service with its limits and
 * today/this-month counters, plus controls (master-admin only) to lift
 * blocks and rotate limits.
 */
import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  Check,
  Loader2,
  Pencil,
  ShieldAlert,
  Sparkles,
  Unlock,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Card, fmtDateTime } from "./_shared";

const LIMIT_FIELDS = [
  { key: "per_user_day", label: "Per user / day" },
  { key: "per_user_month", label: "Per user / month" },
  { key: "global_day", label: "Global / day" },
  { key: "global_month", label: "Global / month" },
];

function StatCell({ count, limit }) {
  if (!limit) {
    return <span className="text-slate-400">∞</span>;
  }
  const pct = Math.min(100, Math.round((count / limit) * 100));
  const tone =
    pct >= 90
      ? "text-rose-700 bg-rose-50 border-rose-200"
      : pct >= 60
        ? "text-amber-700 bg-amber-50 border-amber-200"
        : "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs ${tone}`}
    >
      {count}/{limit}
      <span className="text-[10px] text-slate-400">· {pct}%</span>
    </span>
  );
}

function LiftDialog({ service, onClose, onLifted }) {
  const [note, setNote] = useState("");
  const [limits, setLimits] = useState(() => ({ ...service.limits }));
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        note,
        ...Object.fromEntries(
          LIMIT_FIELDS.map((f) => [f.key, Number(limits[f.key])]),
        ),
      };
      await api.post(`/admin/usage/${service.service}/lift`, body);
      toast.success(`Block lifted on ${service.label}`);
      onLifted();
      onClose();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to lift block.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4"
      data-testid="usage-lift-dialog"
    >
      <Card className="w-full max-w-lg p-6">
        <header className="flex items-start gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-rose-100 text-rose-700">
            <Unlock className="h-4 w-4" />
          </span>
          <div>
            <h2 className="text-base font-medium text-slate-900">
              Lift block on {service.label}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              This service is currently blocked. Set new limits and add a
              short note explaining why you're re-arming it. The action is
              audit-logged with your email.
            </p>
          </div>
        </header>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {LIMIT_FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="block text-xs font-medium text-slate-700 mb-1">
                {f.label}
              </span>
              <input
                type="number"
                min={0}
                value={limits[f.key]}
                onChange={(e) =>
                  setLimits({ ...limits, [f.key]: e.target.value })
                }
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                data-testid={`usage-lift-input-${f.key}`}
              />
            </label>
          ))}
        </div>

        <label className="mt-4 block">
          <span className="block text-xs font-medium text-slate-700 mb-1">
            Note (saved to the audit log)
          </span>
          <textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. higher demand from a marketing push — raising limits temporarily"
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
            data-testid="usage-lift-note"
          />
        </label>

        <footer className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-3 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
            data-testid="usage-lift-confirm"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            Lift block &amp; save new limits
          </button>
        </footer>
      </Card>
    </div>
  );
}

function RotateDialog({ service, onClose, onSaved }) {
  const [limits, setLimits] = useState(() => ({ ...service.limits }));
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(
        LIMIT_FIELDS.map((f) => [f.key, Number(limits[f.key])]),
      );
      await api.post(`/admin/usage/${service.service}/limits`, body);
      toast.success(`Limits updated for ${service.label}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to update limits.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4"
      data-testid="usage-rotate-dialog"
    >
      <Card className="w-full max-w-lg p-6">
        <header className="flex items-start gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
            <Pencil className="h-4 w-4" />
          </span>
          <div>
            <h2 className="text-base font-medium text-slate-900">
              Adjust limits — {service.label}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Use 0 to disable a particular axis. Changes apply immediately.
            </p>
          </div>
        </header>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {LIMIT_FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="block text-xs font-medium text-slate-700 mb-1">
                {f.label}
              </span>
              <input
                type="number"
                min={0}
                value={limits[f.key]}
                onChange={(e) =>
                  setLimits({ ...limits, [f.key]: e.target.value })
                }
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                data-testid={`usage-rotate-input-${f.key}`}
              />
            </label>
          ))}
        </div>
        <footer className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
            data-testid="usage-rotate-confirm"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            Save new limits
          </button>
        </footer>
      </Card>
    </div>
  );
}

function ServiceRow({ service, isMaster, onLift, onRotate }) {
  const blocked = service.is_blocked;
  return (
    <tr
      className={blocked ? "bg-rose-50/40" : undefined}
      data-testid={`usage-row-${service.service}`}
    >
      <td className="py-3 pr-4">
        <div className="font-medium text-slate-900">{service.label}</div>
        <div className="text-xs text-slate-500 font-mono">{service.service}</div>
        {service.cost_note ? (
          <div className="text-[11px] text-slate-400 mt-0.5">
            {service.cost_note}
          </div>
        ) : null}
      </td>
      <td className="py-3 px-4 align-top">
        <StatCell
          count={service.counts.global_today}
          limit={service.limits.global_day}
        />
      </td>
      <td className="py-3 px-4 align-top">
        <StatCell
          count={service.counts.global_this_month}
          limit={service.limits.global_month}
        />
      </td>
      <td className="py-3 px-4 align-top text-xs text-slate-600 leading-tight">
        {LIMIT_FIELDS.map((f) => (
          <div key={f.key}>
            <span className="text-slate-400">{f.label}:</span>{" "}
            <span className="font-mono">{service.limits[f.key] || "—"}</span>
          </div>
        ))}
      </td>
      <td className="py-3 pl-4 align-top text-right">
        {blocked ? (
          <div className="space-y-1.5">
            <span
              className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-1 text-[11px] font-medium text-rose-800"
              data-testid={`usage-blocked-${service.service}`}
            >
              <AlertTriangle className="h-3 w-3" /> Blocked
            </span>
            <div className="text-[10px] text-rose-700 max-w-[180px] leading-snug ml-auto">
              {service.block?.reason} · {fmtDateTime(service.block?.created_at)}
            </div>
            <button
              type="button"
              onClick={() => onLift(service)}
              disabled={!isMaster}
              title={
                isMaster
                  ? "Lift the block and set new limits"
                  : "Only the master admin can lift blocks"
              }
              className="inline-flex items-center gap-1 rounded-lg bg-rose-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`usage-lift-button-${service.service}`}
            >
              <Unlock className="h-3 w-3" /> Lift block
            </button>
          </div>
        ) : (
          <div className="space-y-1.5">
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-medium text-emerald-800">
              <Check className="h-3 w-3" /> Healthy
            </span>
            <button
              type="button"
              onClick={() => onRotate(service)}
              disabled={!isMaster}
              title={
                isMaster
                  ? "Edit limits"
                  : "Only the master admin can adjust quotas"
              }
              className="ml-auto inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`usage-rotate-button-${service.service}`}
            >
              <Pencil className="h-3 w-3" /> Adjust
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function AdminUsage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liftTarget, setLiftTarget] = useState(null);
  const [rotateTarget, setRotateTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/usage");
      setData(data);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to load usage.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !data) {
    return (
      <AdminLayout title="Spend Guard" subtitle="Loading…">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </AdminLayout>
    );
  }

  const blockedCount = data.services.filter((s) => s.is_blocked).length;

  return (
    <AdminLayout
      title="Spend Guard"
      subtitle="Per-user and global quotas for every paid third-party service. When a global cap is hit, the service is blocked until the master admin lifts it."
    >
      <div className="space-y-4" data-testid="admin-usage">
        <Card
          className="p-4 flex flex-wrap items-center gap-3"
          testid="admin-usage-summary"
        >
          <span
            className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${
              blockedCount > 0
                ? "bg-rose-100 text-rose-700"
                : "bg-emerald-100 text-emerald-700"
            }`}
          >
            {blockedCount > 0 ? (
              <ShieldAlert className="h-4 w-4" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900">
              {blockedCount > 0
                ? `${blockedCount} service${blockedCount === 1 ? "" : "s"} currently blocked`
                : "All services healthy"}
            </p>
            <p className="text-xs text-slate-500">
              Master admin: <span className="font-mono">{data.master_admin_email || "(unset)"}</span>
              {data.you_are_master
                ? " · you can lift blocks and adjust limits"
                : " · only the master admin can lift blocks"}
            </p>
          </div>
        </Card>

        <Card className="overflow-hidden" testid="admin-usage-table-card">
          <table
            className="w-full text-sm"
            data-testid="admin-usage-table"
          >
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left font-medium py-3 pl-5 pr-4 w-1/3">
                  Service
                </th>
                <th className="text-left font-medium py-3 px-4">
                  Today (global)
                </th>
                <th className="text-left font-medium py-3 px-4">
                  This month (global)
                </th>
                <th className="text-left font-medium py-3 px-4">Limits</th>
                <th className="text-right font-medium py-3 pl-4 pr-5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 [&_td]:pl-5 [&_td]:pr-5">
              {data.services.map((s) => (
                <ServiceRow
                  key={s.service}
                  service={s}
                  isMaster={data.you_are_master}
                  onLift={setLiftTarget}
                  onRotate={setRotateTarget}
                />
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {liftTarget && (
        <LiftDialog
          service={liftTarget}
          onClose={() => setLiftTarget(null)}
          onLifted={load}
        />
      )}
      {rotateTarget && (
        <RotateDialog
          service={rotateTarget}
          onClose={() => setRotateTarget(null)}
          onSaved={load}
        />
      )}
    </AdminLayout>
  );
}
